/**
 * Create Challenge Data Builders
 * Functions for building and transforming data for challenge creation
 */

import type { Game, PickSlip, Friend } from '../types/create.types';
import { formatShortDate } from '../../../helpers/dateFormatters.helper';

/**
 * Auto-generate challenge name based on context
 */
export const generateChallengeName = (
  selectedGames: Game[],
  pickSlips: PickSlip[],
  selectedFriends: Friend[]
): string => {
  const now = new Date();
  const dateStr = formatShortDate(now);

  // Get unique leagues from selected games
  const selectedGameObjects = selectedGames.filter(g => pickSlips.some(b => b.gameId === g.id));
  const uniqueLeagues = Array.from(new Set(selectedGameObjects.map(g => g.league)));

  // Strategy 1: If single special match (like El Clásico, Derby, etc.)
  if (selectedGameObjects.length === 1) {
    const game = selectedGameObjects[0];
    return `${game.homeTeam} vs ${game.awayTeam}`;
  }

  // Strategy 2: If all matches from same league
  if (uniqueLeagues.length === 1) {
    const leagueName = uniqueLeagues[0];
    const matchCount = selectedGameObjects.length;
    return `${leagueName} - ${matchCount} Match${matchCount !== 1 ? 'es' : ''} (${dateStr})`;
  }

  // Strategy 3: Multiple leagues
  if (uniqueLeagues.length > 1) {
    const matchCount = selectedGameObjects.length;
    return `Mixed Challenge - ${matchCount} Matches (${dateStr})`;
  }

  // Fallback: Generic name with friends count
  const friendCount = selectedFriends.length;
  return `Challenge with ${friendCount} Friend${friendCount !== 1 ? 's' : ''} (${dateStr})`;
};

